// src/components/CareersNav.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Tooltip, Modal, Form, Input, Button, Alert } from "antd";
import { SearchOutlined, UserOutlined } from "@ant-design/icons";
import {
  candMe,
  candLogin,
  candRegister,
  getCandidateToken,
  getCandidateAccount,
  API_ROOT,
  candForgotPassword,
} from "../services/api";

/* -------------------------------
   Helpers
--------------------------------*/

// Normalize logo URLs so that /uploads/... loads from the current origin.
// - Relative like "uploads/encreez/logo.png" -> "/uploads/encreez/logo.png"
// - Absolute with /uploads/... -> force pathname (same-origin)
// - External/CDN not under /uploads -> keep absolute URL
function normalizeLogoUrl(logoUrl) {
  if (!logoUrl) return "";

  let raw = String(logoUrl).trim();

  // If it's already relative, ensure it starts with "/"
  if (!/^https?:\/\//i.test(raw)) {
    if (!raw.startsWith("/")) raw = "/" + raw; // "uploads/..." -> "/uploads/..."
    return raw;
  }

  // Absolute URL
  try {
    const u = new URL(raw);
    if (u.pathname.startsWith("/uploads/")) {
      // Use same-origin by stripping host, keeping path/query/hash
      return u.pathname + u.search + u.hash;
    }
    // External CDN or other path → keep as-is
    return u.href;
  } catch {
    return raw;
  }
}

function toAbsoluteUrl(u) {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  return `${API_ROOT}${u.startsWith("/") ? u : `/${u}`}`;
}
function pickAvatarUrl(c) {
  return c?.avatar_full_url || c?.avatar_url || c?.candidate_avatar_url || c?.candidate_avatar || "";
}

/* ------------------------------
   Auth Modal
--------------------------------*/
function AuthModal({ open, onClose, orgId, token, onSignedIn }) {
  const [mode, setMode] = useState("login"); // 'login' | 'register' | 'forgot'
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [form] = Form.useForm();

  async function handleSubmit(values) {
    setBusy(true);
    setNotice(null);
    try {
      if (mode === "register") {
        await candRegister({ orgId, token, name: values.name, email: values.email, password: values.password });
        setNotice({
          type: "success",
          msg: "Registration successful. We’ve sent a verification email. Please verify, then sign in.",
        });
      } else if (mode === "login") {
        const { candidate } = await candLogin({ orgId, token, email: values.email, password: values.password });
        onSignedIn?.(candidate);
        onClose?.();
      } else if (mode === "forgot") {
        await candForgotPassword({ orgId, token, email: values.email });
        setNotice({
          type: "success",
          msg: "If an account exists, a password reset link has been sent to that email.",
        });
      }
    } catch (e) {
      setNotice({ type: "error", msg: e.message || "Request failed" });
    } finally {
      setBusy(false);
    }
  }

  const footerLinks =
    mode === "login" ? (
      <div style={{ display: "flex", gap: 12 }}>
        <Button type="link" onClick={() => setMode("register")}>
          New user? Register
        </Button>
        <Button type="link" onClick={() => setMode("forgot")}>
          Forgot password?
        </Button>
      </div>
    ) : mode === "register" ? (
      <Button type="link" onClick={() => setMode("login")}>
        Have an account? Sign in
      </Button>
    ) : (
      <Button type="link" onClick={() => setMode("login")}>
        Back to sign in
      </Button>
    );

  return (
    <Modal
      title={mode === "register" ? "Create your account" : mode === "forgot" ? "Reset your password" : "Sign in"}
      open={open}
      onCancel={() => !busy && onClose?.()}
      footer={null}
      destroyOnHidden
      maskClosable={!busy}
    >
      {notice && <Alert type={notice.type} message={notice.msg} showIcon style={{ marginBottom: 12 }} />}
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        {mode === "register" && (
          <Form.Item label="Full name" name="name" rules={[{ required: true, message: "Please enter your name" }]}>
            <Input placeholder="Jane Doe" />
          </Form.Item>
        )}
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: "Please enter your email" },
            { type: "email", message: "Please enter a valid email" },
          ]}
        >
          <Input placeholder="jane@example.com" />
        </Form.Item>
        {mode !== "forgot" && (
          <Form.Item label="Password" name="password" rules={[{ required: true, message: "Please enter your password" }]}>
            <Input.Password placeholder="••••••••" />
          </Form.Item>
        )}

        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <Button type="primary" htmlType="submit" loading={busy}>
            {mode === "register" ? "Register" : mode === "forgot" ? "Send reset link" : "Sign in"}
          </Button>
          <div>{footerLinks}</div>
        </div>
      </Form>
    </Modal>
  );
}

/* ------------------------------
   Navbar
--------------------------------*/
export default function CareersNav({ org, orgId, token }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Only include token in query if valid (avoid token= empty in URLs)
  const qs = useMemo(() => {
    const u = new URLSearchParams();
    if (orgId != null) u.set("orgId", String(orgId));
    if (token && token !== "null" && token !== "NULL") u.set("token", token);
    const s = u.toString();
    return s ? `?${s}` : "";
  }, [orgId, token]);

  // Preserve hash (#token=...) on top-level links
  const hash = typeof window !== "undefined" ? window.location.hash || "" : "";

  const [authOpen, setAuthOpen] = useState(false);
  const [me, setMe] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState("");

  // Load candidate info/avatar when signed in
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!getCandidateToken()) {
        setMe(null);
        setAvatarUrl("");
        return;
      }
      const basic = await candMe();
      if (cancelled) return;
      setMe(basic);
      try {
        const acc = await getCandidateAccount();
        const c = acc?.candidate || {};
        const url = toAbsoluteUrl(pickAvatarUrl(c));
        if (!cancelled) setAvatarUrl(url);
      } catch {
        // ignore
      }
    }
    load();
    const onStorage = (e) => {
      if (e.key === "careers_cand_token") load();
    };
    const onAvatarUpdate = (e) => setAvatarUrl(e?.detail?.url || "");
    window.addEventListener("storage", onStorage);
    window.addEventListener("careers:avatar-updated", onAvatarUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("careers:avatar-updated", onAvatarUpdate);
    };
  }, []);

  async function handleAccountClick() {
    if (getCandidateToken()) {
      const current = await candMe();
      if (current) {
        navigate(`/account${qs}${hash}`);
        return;
      }
    }
    setAuthOpen(true);
  }

  const accountTitle = me?.name ? `Signed in as ${me.name}` : "My Account";

  return (
    <>
      <div style={{ position: "sticky", top: 0, zIndex: 10, background: "#fff", borderBottom: "1px solid #eee" }}>
        <div
          className="page-wrap"
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, paddingTop: 0, paddingBottom: 0 }}
        >
          <Link to={`/${qs}${hash}`} style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none" }}>
          </Link>
          <nav style={{ display: "flex", gap: 16 }}>
            <Tooltip title="Job Search">
              <Link to={`/${qs}${hash}`} aria-label="Job Search" style={{ color: pathname === "/" ? "#1677ff" : "#111827" }}>
                <SearchOutlined style={{ fontSize: 20 }} />
              </Link>
            </Tooltip>

            <Tooltip title={accountTitle}>
              <button
                type="button"
                onClick={handleAccountClick}
                aria-label="My Account"
                style={{ background: "transparent", border: 0, cursor: "pointer", color: pathname === "/account" ? "#1677ff" : "#111827", padding: 0, lineHeight: 1 }}
              >
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={me?.name || "avatar"}
                    style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover", display: "block" }}
                  />
                ) : (
                  <UserOutlined style={{ fontSize: 20 }} />
                )}
              </button>
            </Tooltip>
          </nav>
        </div>
      </div>

      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        orgId={orgId}
        token={token}
        onSignedIn={async () => {
          setAuthOpen(false);
          try {
            const basic = await candMe();
            setMe(basic);
            const acc = await getCandidateAccount();
            const c = acc?.candidate || {};
            setAvatarUrl(toAbsoluteUrl(pickAvatarUrl(c)));
          } catch {
            // ignore
          }
          navigate(`/account${qs}${hash}`);
        }}
      />
    </>
  );
}
